import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { api, errorMessage } from '../api.js'
import { combine, optimistic } from '../optimistic.js'
import { useToast } from '../../components/ui/Toast.jsx'
import { qk } from './keys.js'

// ---- Reads ------------------------------------------------------------------

export function useSystems() {
  return useQuery({
    queryKey: qk.systems,
    queryFn: ({ signal }) => api('/systems', { signal }).then((d) => d.systems),
    staleTime: 60_000,
  })
}

/** Map slug → system (empty map while loading). */
export function useSystemsMap() {
  const { data } = useSystems()
  return useMemo(() => new Map((data ?? []).map((s) => [s.slug, s])), [data])
}

export function useSystemDetail(slug, version = 'current') {
  return useQuery({
    queryKey: qk.system(slug, version),
    queryFn: ({ signal }) => api(`/systems/${slug}?version=${version}`, { signal }),
    enabled: Boolean(slug),
  })
}

export function useSkillFile(slug, version, path) {
  return useQuery({
    queryKey: qk.systemFile(slug, version, path),
    queryFn: ({ signal }) =>
      api(`/systems/${slug}/file?${new URLSearchParams({ path, version })}`, { signal }),
    enabled: Boolean(slug && path),
    staleTime: 5 * 60_000,
  })
}

export function useSystemVersions(slug) {
  return useQuery({
    queryKey: qk.versions(slug),
    queryFn: ({ signal }) => api(`/systems/${slug}/versions`, { signal }).then((d) => d.versions),
    enabled: Boolean(slug),
  })
}

function invalidateSystem(queryClient, slug) {
  queryClient.invalidateQueries({ queryKey: qk.systems })
  queryClient.invalidateQueries({ queryKey: qk.systemDetails(slug) })
  queryClient.invalidateQueries({ queryKey: qk.versions(slug) })
}

// ---- System create / update -------------------------------------------------

/** Optimistic card: appears in the library list immediately. */
export function useCreateSystem() {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: (input) => api('/systems', { method: 'POST', body: input }).then((d) => d.system),
    onMutate: async (input) => {
      const placeholder = {
        id: `optimistic-${input.slug}`,
        slug: input.slug,
        name: input.name,
        shortName: input.shortName ?? input.name,
        letter: (input.letter ?? input.name[0]).toUpperCase(),
        vendor: input.vendor ?? null,
        vendorShort: input.vendorShort ?? null,
        category: input.category,
        categoryLabel: input.categoryLabel,
        tagline: input.tagline ?? null,
        description: input.description ?? null,
        palette: input.palette ?? 'slate',
        suggestions: [],
        stats: {},
        status: 'none',
        published: null,
        draft: null,
        sortOrder: Number.MAX_SAFE_INTEGER,
        optimistic: true,
      }
      const rollback = await optimistic(queryClient, { queryKey: qk.systems }, (list) => [...list, placeholder])
      return { rollback }
    },
    onSuccess: (system) => {
      queryClient.setQueryData(qk.systems, (list) =>
        list?.map((s) => (s.slug === system.slug ? system : s)) ?? [system],
      )
    },
    onError: (error, _input, context) => {
      context?.rollback()
      toast.error(`Couldn't add the system — ${errorMessage(error)}`)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.systems }),
  })
}

export function useUpdateSystem(slug) {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: (patch) => api(`/systems/${slug}`, { method: 'PATCH', body: patch }).then((d) => d.system),
    onMutate: async (patch) => {
      const apply = (system) => ({
        ...system,
        ...patch,
        ...(patch.statsOverride !== undefined ? { stats: { ...system.stats, ...(patch.statsOverride ?? {}) } } : {}),
      })
      const rollbackList = await optimistic(queryClient, { queryKey: qk.systems }, (list) =>
        list.map((s) => (s.slug === slug ? apply(s) : s)),
      )
      const rollbackDetail = await optimistic(queryClient, { queryKey: qk.systemDetails(slug) }, (data) => ({
        ...data,
        system: apply(data.system),
      }))
      return { rollback: combine(rollbackList, rollbackDetail) }
    },
    onError: (error, _patch, context) => {
      context?.rollback()
      toast.error(`Couldn't save — ${errorMessage(error)}`)
    },
    onSettled: () => invalidateSystem(queryClient, slug),
  })
}

/** Optimistic: the card leaves the library list immediately. Admins only. */
export function useDeleteSystem(slug) {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: () => api(`/systems/${slug}`, { method: 'DELETE' }),
    onMutate: async () => {
      const rollback = await optimistic(queryClient, { queryKey: qk.systems }, (list) => list.filter((s) => s.slug !== slug))
      return { rollback }
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: qk.systemDetails(slug) })
      queryClient.removeQueries({ queryKey: qk.versions(slug) })
      queryClient.removeQueries({ queryKey: qk.systemFiles(slug) })
    },
    onError: (error, _vars, context) => {
      context?.rollback()
      toast.error(`Couldn't delete the system — ${errorMessage(error)}`)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.systems }),
  })
}

// ---- Draft editing ----------------------------------------------------------

/**
 * Save a skill file into the draft. Optimistic on both the file body cache and
 * the draft file list; `baseSha` makes concurrent edits a 409 instead of a
 * silent overwrite.
 */
export function useSaveSkillFile(slug) {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: ({ path, content, summary, baseSha }) =>
      api(`/systems/${slug}/draft/file`, { method: 'PUT', body: { path, content, summary, baseSha } }),
    onMutate: async ({ path, content, summary }) => {
      const bytes = new TextEncoder().encode(content).length
      const rollbackBody = await optimistic(queryClient, { queryKey: qk.systemFile(slug, 'draft', path) }, (data) => ({
        ...data,
        content,
        file: { ...data.file, bytes, ...(summary !== undefined ? { summary } : {}) },
      }))
      const rollbackList = await optimistic(queryClient, { queryKey: qk.system(slug, 'draft') }, (data) => {
        const exists = data.files.some((f) => f.path === path)
        const files = exists
          ? data.files.map((f) =>
              f.path === path ? { ...f, bytes, updatedAt: Date.now(), ...(summary !== undefined ? { summary } : {}) } : f,
            )
          : [...data.files, { path, role: 'other', summary: summary ?? null, sectionLabel: null, bytes, updatedAt: Date.now(), pending: true }]
        return { ...data, files }
      })
      return { rollback: combine(rollbackBody, rollbackList) }
    },
    onSuccess: ({ file }) => {
      queryClient.setQueryData(qk.systemFile(slug, 'draft', file.path), (data) =>
        data ? { ...data, file: { ...data.file, ...file, index: undefined } } : data,
      )
    },
    onError: (error, _vars, context) => {
      context?.rollback()
      if (error?.status === 409) {
        toast.error('Someone else changed this file. Reload it to see their version, then re-apply your edit.')
      } else {
        toast.error(`Not saved — ${errorMessage(error)}`)
      }
    },
    onSettled: (_data, _error, { path }) => {
      queryClient.invalidateQueries({ queryKey: qk.systemFile(slug, 'draft', path) })
      invalidateSystem(queryClient, slug)
    },
  })
}

export function useDeleteSkillFile(slug) {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: ({ path }) =>
      api(`/systems/${slug}/draft/file?${new URLSearchParams({ path })}`, { method: 'DELETE' }),
    onMutate: async ({ path }) => {
      const rollback = await optimistic(queryClient, { queryKey: qk.system(slug, 'draft') }, (data) => ({
        ...data,
        files: data.files.filter((f) => f.path !== path),
      }))
      return { rollback }
    },
    onError: (error, _vars, context) => {
      context?.rollback()
      toast.error(`Couldn't remove the file — ${errorMessage(error)}`)
    },
    onSettled: () => invalidateSystem(queryClient, slug),
  })
}

export function useImportDraft(slug) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ files, replace }) =>
      api(`/systems/${slug}/draft/import`, { method: 'POST', body: { files, replace } }),
    onSettled: () => invalidateSystem(queryClient, slug),
  })
}

export function usePublishDraft(slug) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ label, notes }) =>
      api(`/systems/${slug}/draft/publish`, { method: 'POST', body: { label, notes } }).then((d) => d.system),
    onSuccess: (system) => {
      queryClient.setQueryData(qk.systems, (list) => list?.map((s) => (s.slug === slug ? system : s)))
    },
    onSettled: () => {
      invalidateSystem(queryClient, slug)
      queryClient.invalidateQueries({ queryKey: qk.systemFiles(slug) })
    },
  })
}

export function useDiscardDraft(slug) {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: () => api(`/systems/${slug}/draft`, { method: 'DELETE' }).then((d) => d.system),
    onMutate: async () => {
      const rollback = await optimistic(queryClient, { queryKey: qk.systems }, (list) =>
        list.map((s) => (s.slug === slug ? { ...s, draft: null, status: s.published ? 'ready' : 'none' } : s)),
      )
      return { rollback }
    },
    onError: (error, _vars, context) => {
      context?.rollback()
      toast.error(`Couldn't discard the draft — ${errorMessage(error)}`)
    },
    onSettled: () => {
      invalidateSystem(queryClient, slug)
      queryClient.invalidateQueries({ queryKey: qk.systemFiles(slug) })
    },
  })
}
